import React, { FC } from 'react';
import { GetChangeSummaryResponseType } from '@clearcover/core-app-types';
import { Table } from 'Shared/components';
import styles from './styles.module.css';
import { formatMoney, EMDASH } from 'Shared/utils';

export type CoverageSectionType = {
  coverages: GetChangeSummaryResponseType['coverages'];
};

type CoveragePremiumChangeType = {
  name: string;
  selectedCoverage: string;
  fullTermWrittenPremium: number;
  fullTermWrittenPremiumDifference: number;
};

export type CoverageSectionTableType = {
  name: string;
  coverages: Record<string, CoveragePremiumChangeType>;
  endorsements: Array<
    Pick<CoveragePremiumChangeType, 'name' | 'selectedCoverage'>
  >;
  totalPremium: string;
  totalChange: string;
  isVehicleSummary?: boolean;
};

export const CoverageSectionTable: FC<CoverageSectionTableType> = ({
  coverages,
  endorsements,
  totalChange,
  totalPremium,
  name,
  isVehicleSummary = false,
}) => {
  return (
    <>
      <Table
        headers={[
          `${name}`,
          'Selected Coverage',
          'Rated Premium',
          'Changed Premium',
        ]}
        headerClassName={styles.policyCoverageSummarySectionTableHeader}
        className={styles.policyCoverageSummarySectionTable}
        columnWidths={[2, 1, 1, 1]}
        striped={false}
        rows={[
          ...Object.keys(coverages).map((coverageId) => {
            const {
              name,
              selectedCoverage,
              fullTermWrittenPremium,
              fullTermWrittenPremiumDifference,
            } = coverages[coverageId];
            return {
              key: name,
              className: styles.policyCoverageSummarySectionTableRow,
              data: [
                name,
                selectedCoverage,
                formatMoney(fullTermWrittenPremium),
                formatMoney(fullTermWrittenPremiumDifference),
              ],
            };
          }),
          ...endorsements.map((endorsement) => ({
            key: endorsement.name,
            className: styles.policyCoverageSummarySectionTableRow,
            data: [
              endorsement.name,
              endorsement.selectedCoverage,
              EMDASH,
              EMDASH,
            ],
          })),
          {
            key: 'coverages-endorsements-total',
            className: styles.policyCoverageSummarySectionTableRow,
            data: [
              '',
              '',
              `Total ${isVehicleSummary ? 'Vehicle' : ''} Premium ${formatMoney(
                totalPremium
              )}`,
              `Total Change ${formatMoney(totalChange)}`,
            ],
          },
        ]}
      />
    </>
  );
};

export const CoverageSection: FC<CoverageSectionType> = ({ coverages }) => {
  const { policyCoverage, vehicles } = coverages;
  const policy = {
    name: 'POLICY COVERAGES',
    totalChange: policyCoverage.totalChange,
    totalPremium: policyCoverage.totalPremium,
    coverages: policyCoverage.coverages,
    endorsements: policyCoverage.endorsements,
  };

  return (
    <div data-testid="policy-summary-coverages-section">
      <CoverageSectionTable {...policy} />
      {Object.keys(vehicles).length > 0 &&
        Object.keys(vehicles).map((vehicleName, index) => {
          const { totalChange, totalPremium, coverages, endorsements } =
            vehicles[vehicleName];
          const vehicleCoverage: CoverageSectionTableType = {
            name: vehicleName,
            totalChange,
            totalPremium,
            coverages,
            endorsements,
            isVehicleSummary: true,
          };
          return <CoverageSectionTable key={index} {...vehicleCoverage} />;
        })}
    </div>
  );
};
